import React, { useCallback, useEffect, useRef, useState } from 'react';
import type ReactPlayerInstance from 'react-player';
import { useInView } from '@/ui/hooks/use-in-view';
import { Fullscreen, useFullscreen } from './use-fullscreen';
import { VideoMediaEvents, useVideoMediaEvents } from './use-video-media-events';
import { VideoPlayback, VideoPlayerMode, useVideoPlayback } from './use-video-playback';

export interface UseVideoPlayerStateParams {
  mode: VideoPlayerMode;
  isActive?: boolean;
  loop?: boolean;
  onPlayingChange?: (playing: boolean) => void;
  onEnded?: () => void;
}

export interface VideoPlayerState {
  wrapperRef: React.RefObject<HTMLDivElement>;
  /** react-player v2 hands the media element over in `onReady`, never through a `ref`. */
  onPlayerReady: (player: ReactPlayerInstance) => void;
  playback: VideoPlayback;
  media: VideoMediaEvents;
  fullscreen: Fullscreen;
  /** The player is torn down again after a fatal media error, leaving only the poster. */
  isPlayerMounted: boolean;
}

/** Bundles the four independent hooks a `VideoPlayer` needs into a single state object. */
export function useVideoPlayerState({
  mode,
  isActive,
  loop,
  onPlayingChange,
  onEnded,
}: UseVideoPlayerStateParams): VideoPlayerState {
  // Only the automatic modes act on visibility at all, and only their mount has to beat
  // react-player's lazy loader, so nothing else pays for a synchronous commit: see
  // `UseInViewOptions.sync`.
  const isAutomaticMode = mode === 'in-view' || mode === 'background';
  const [wrapperRef, isInView] = useInView<HTMLDivElement>({
    rootMargin: '200px',
    sync: isAutomaticMode,
  });
  const playerRef = useRef<HTMLVideoElement | null>(null);
  // Readiness is state, not a ref: capturing the element has to re-render the fullscreen support
  // detection, which differs before and after a successful load.
  const [isMediaReady, setIsMediaReady] = useState(false);
  const playback = useVideoPlayback({ mode, isActive, isInView });
  const media = useVideoMediaEvents({
    mode,
    loop,
    playerRef,
    setPlaying: playback.setPlaying,
    onPlayingChange,
    onEnded,
  });
  const fullscreen = useFullscreen(wrapperRef, playerRef, isMediaReady);
  const isPlayerMounted = playback.shouldMount && !media.hasFatalError;

  const onPlayerReady = useCallback((player: ReactPlayerInstance) => {
    const internalPlayer = player.getInternalPlayer();
    // YouTube and Vimeo hand back their SDK objects, which offer none of the media element API
    // the fullscreen fallback and the fatal-error check rely on.
    playerRef.current = internalPlayer instanceof HTMLVideoElement ? internalPlayer : null;
    setIsMediaReady(true);
  }, []);

  // A reference to an unmounted element would keep reporting stale fullscreen support.
  useEffect(() => {
    if (!isPlayerMounted) {
      playerRef.current = null;
      setIsMediaReady(false);
    }
  }, [isPlayerMounted]);

  return {
    wrapperRef,
    onPlayerReady,
    playback,
    media,
    fullscreen,
    isPlayerMounted,
  };
}
